import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RotateCcw, Loader2, Download, FileSpreadsheet } from "lucide-react";
import { FormFooter } from "@/components/FormFooter";
import { toast } from "sonner";
import { systemConfigAPI } from "@/lib/api";
import { NumberField } from "@/components/ui/number-field";

interface ExcelImportConfigProps {
  onSave?: (config: ExcelColumnMapping) => void;
}

export interface ExcelColumnMapping {
  codigo: string;
  nombre: string;
  proveedor: string;
  grupo: string;
  subgrupo: string;
  cantidad: string;
  sucursal: string;
  deposito: string;
  precioBase: string;
  precioLista: string;
  iva: string;
}

const EXCEL_COLUMNS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T'];

const DEFAULT_MAPPING: ExcelColumnMapping = {
  codigo: '',
  nombre: '',
  proveedor: '',
  grupo: '',
  subgrupo: '',
  cantidad: '',
  sucursal: '',
  deposito: '',
  precioBase: '',
  precioLista: '',
  iva: ''
};

export function ExcelImportConfig({ onSave }: ExcelImportConfigProps) {
  const [mapping, setMapping] = useState<ExcelColumnMapping>(DEFAULT_MAPPING);
  const [exportingList, setExportingList] = useState<'csv' | 'text' | null>(null);
  const [startRow, setStartRow] = useState(1); // Row number where headers are (1-indexed)
  const [_isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // console.log('ExcelImportConfig rendering', mapping);

  // Load saved configuration from API
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        const config = await systemConfigAPI.getExcelImportConfig();
        if (config && config.mapping) {
          // Normaliza snake_case legacy (precio_base/start_row) → camelCase del form
          const raw = config.mapping as ExcelColumnMapping & {
            precio_base?: string;
            precio_lista?: string;
          };
          const mapping: ExcelColumnMapping = {
            ...DEFAULT_MAPPING,
            ...raw,
            precioBase: raw.precioBase || raw.precio_base || '',
            precioLista: raw.precioLista || raw.precio_lista || '',
          };
          const rowCfg = config as { startRow?: number; start_row?: number };
          const row = Number(rowCfg.startRow ?? rowCfg.start_row ?? 1) || 1;
          setMapping(mapping);
          setStartRow(row);
          localStorage.setItem('excelImportMapping', JSON.stringify(mapping));
          localStorage.setItem('excelImportStartRow', String(row));
        } else {
          // Fallback to localStorage if API returns empty
          const savedMapping = localStorage.getItem('excelImportMapping');
          if (savedMapping) {
            setMapping(JSON.parse(savedMapping));
          }
          const savedStartRow = localStorage.getItem('excelImportStartRow');
          if (savedStartRow) {
            setStartRow(parseInt(savedStartRow) || 1);
          }
        }
      } catch (error) {
        console.error('Error loading config from API:', error);
        // Fallback to localStorage
        const savedMapping = localStorage.getItem('excelImportMapping');
        if (savedMapping) {
          try {
            setMapping(JSON.parse(savedMapping));
          } catch (parseErr) {
            console.error('Error loading saved mapping:', parseErr);
          }
        }
        const savedStartRow = localStorage.getItem('excelImportStartRow');
        if (savedStartRow) {
          setStartRow(parseInt(savedStartRow) || 1);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadConfig();
  }, []);

  const updateMapping = (field: keyof ExcelColumnMapping, value: string) => {
    setMapping(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!mapping.nombre || !mapping.proveedor) {
      toast.error("Nombre y Proveedor son campos obligatorios");
      return;
    }

    // Check for duplicate column assignments
    const selectedColumns: Record<string, string[]> = {};
    Object.entries(mapping).forEach(([field, column]) => {
      if (column) {
        if (!selectedColumns[column]) {
          selectedColumns[column] = [];
        }
        selectedColumns[column].push(field);
      }
    });

    const duplicates = Object.entries(selectedColumns).filter(([, fields]) => fields.length > 1);
    if (duplicates.length > 0) {
      const duplicateMessages = duplicates.map(([col, fields]) =>
        `Columna ${col}: ${fields.join(', ')}`
      ).join('\n');
      toast.error(`No puede asignar la misma columna a múltiples campos:\n${duplicateMessages}`);
      return;
    }

    // Save to localStorage as cache
    localStorage.setItem('excelImportMapping', JSON.stringify(mapping));
    localStorage.setItem('excelImportStartRow', startRow.toString());

    // Save to backend API
    setIsSaving(true);
    try {
      await systemConfigAPI.setExcelImportConfig({ mapping, startRow });

      if (onSave) {
        onSave(mapping);
      }

      toast.success("Configuración guardada en el servidor exitosamente");
    } catch (error) {
      console.error('Error saving to API:', error);
      toast.error("Error al guardar en servidor, guardado localmente");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setMapping(DEFAULT_MAPPING);
    setStartRow(1);
    localStorage.removeItem('excelImportMapping');
    localStorage.removeItem('excelImportStartRow');

    // Also reset on server
    try {
      await systemConfigAPI.setExcelImportConfig({ mapping: DEFAULT_MAPPING, startRow: 1 });
      toast.success("Configuración restablecida");
    } catch (error) {
      console.error('Error resetting on API:', error);
      toast.success("Configuración restablecida localmente");
    }
  };

  const handleDownloadExportLists = async (output: 'csv' | 'text') => {
    setExportingList(output);
    try {
      await systemConfigAPI.downloadExportLists(output);
      toast.success(output === 'csv' ? 'CSV descargado' : 'TXT descargado');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Error al descargar';
      toast.error(msg.includes('Unauthorized') ? 'Sesión expirada; iniciá sesión de nuevo' : msg);
    } finally {
      setExportingList(null);
    }
  };

  // Get available columns for a specific field (excluding already selected ones)
  const getAvailableColumns = (currentField: keyof ExcelColumnMapping) => {
    const selectedColumns = new Set(
      Object.entries(mapping)
        .filter(([field, value]) => field !== currentField && value)
        .map(([, value]) => value)
    );

    return EXCEL_COLUMNS.filter(col => !selectedColumns.has(col));
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-2">Mapeo de Columnas Excel para Stock</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Configure qué columna del Excel corresponde a cada campo del sistema. Al importar, el sistema:
        </p>
        <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 mb-4">
          <li>Filtrará solo los productos del proveedor especificado</li>
          <li>Actualizará productos existentes (por código o nombre) en lugar de crear duplicados</li>
          <li>Creará nuevos productos solo si no existen</li>
        </ul>
      </div>

      {/* Start Row Configuration */}
      <div className="p-4 border border-input rounded-lg bg-blue-50 dark:bg-blue-950/30 mb-4">
        <Label htmlFor="startRowConfig" className="text-sm font-medium">Fila de Encabezados</Label>
        <p className="text-xs text-muted-foreground mt-1 mb-2">
          Si el Excel tiene filas de metadatos/fórmulas al inicio, indique en qué fila están los encabezados de columna (ej: 6 si hay 5 filas de metadatos)
        </p>
        <NumberField
          id="startRowConfig"
          min="1"
          max="50"
          value={startRow}
          onChange={(e) => setStartRow(Math.max(1, parseInt(e.target.value) || 1))}
          className="w-24 px-3 py-2 border border-input rounded-md text-sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="codigo" className="text-sm">
            Código
          </Label>
          <Select value={mapping.codigo || undefined} onValueChange={(v) => updateMapping('codigo', v)}>
            <SelectTrigger id="codigo" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('codigo').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="nombre" className="flex items-center gap-1 text-sm">
            Nombre <span className="text-red-500">*</span>
          </Label>
          <Select value={mapping.nombre || undefined} onValueChange={(v) => updateMapping('nombre', v)}>
            <SelectTrigger id="nombre" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('nombre').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="proveedor" className="flex items-center gap-1 text-sm">
            Proveedor <span className="text-red-500">*</span>
          </Label>
          <Select value={mapping.proveedor || undefined} onValueChange={(v) => updateMapping('proveedor', v)}>
            <SelectTrigger id="proveedor" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('proveedor').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="grupo" className="text-sm">Grupo</Label>
          <Select value={mapping.grupo || undefined} onValueChange={(v) => updateMapping('grupo', v)}>
            <SelectTrigger id="grupo" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('grupo').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="subgrupo" className="text-sm">Subgrupo</Label>
          <Select value={mapping.subgrupo || undefined} onValueChange={(v) => updateMapping('subgrupo', v)}>
            <SelectTrigger id="subgrupo" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('subgrupo').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="cantidad" className="text-sm">Cantidad</Label>
          <Select value={mapping.cantidad || undefined} onValueChange={(v) => updateMapping('cantidad', v)}>
            <SelectTrigger id="cantidad" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('cantidad').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="sucursal" className="text-sm">Sucursal</Label>
          <Select value={mapping.sucursal || undefined} onValueChange={(v) => updateMapping('sucursal', v)}>
            <SelectTrigger id="sucursal" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('sucursal').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="deposito" className="text-sm">Depósito</Label>
          <Select value={mapping.deposito || undefined} onValueChange={(v) => updateMapping('deposito', v)}>
            <SelectTrigger id="deposito" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('deposito').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="precioBase" className="text-sm">Precio Base</Label>
          <Select value={mapping.precioBase || undefined} onValueChange={(v) => updateMapping('precioBase', v)}>
            <SelectTrigger id="precioBase" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('precioBase').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="precioLista" className="text-sm">Precio Lista</Label>
          <Select value={mapping.precioLista || undefined} onValueChange={(v) => updateMapping('precioLista', v)}>
            <SelectTrigger id="precioLista" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('precioLista').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="iva" className="text-sm">IVA (%)</Label>
          <Select value={mapping.iva || undefined} onValueChange={(v) => updateMapping('iva', v)}>
            <SelectTrigger id="iva" className="h-9">
              <SelectValue placeholder="Columna..." />
            </SelectTrigger>
            <SelectContent>
              {getAvailableColumns('iva').map(col => (
                <SelectItem key={col} value={col}>
                  Columna {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Export Lists Section */}
      <div className="mt-6 pt-4 border-t border-border">
        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" />
          Listas para Validación de Excel
        </h4>
        <p className="text-sm text-muted-foreground mb-3">
          Descargue estas listas para usarlas como opciones de dropdown en su archivo Excel.
          Copie los valores a una hoja "Datos" y use Validación de Datos.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={exportingList !== null}
            onClick={() => handleDownloadExportLists('csv')}
          >
            {exportingList === 'csv' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Descargar CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={exportingList !== null}
            onClick={() => handleDownloadExportLists('text')}
          >
            {exportingList === 'text' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Descargar TXT
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Incluye: Proveedores, Sucursales, Depósitos, Grupos, Subgrupos
        </p>
      </div>

      <div className="pt-4">
        <FormFooter
          extra={
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Restablecer
            </Button>
          }
          onSave={handleSave}
          saveDisabled={isSaving}
          saving={isSaving}
        />
      </div>
    </div>
  );
}